import * as React from 'react';
import axios, { CancelTokenSource } from 'axios';
import clsx from 'clsx';
import { useHistory } from 'react-router';
import themePrimary from '../../styles/themePrimary';
import { makeStyles, Typography, useMediaQuery } from '@material-ui/core';
import { GetReceiptResponse } from '../../models/postcheckout/GetReceiptResponse';
import FullWidthButton, { FullWidthButtonProps, FullWidthButtonSize } from '../common/Buttons/FullWidthButton';
import { Receipt } from '../../models/postcheckout/Receipt';
import CheckReceipt from './CheckReceipt';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { CheckOutState, CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import { String_System_GeneralError, String_System_ConnectionError, String_ViewReceipt_EmailReceiptButton, 
    String_SendReceiptLink_SuccessMessage, String_ViewReceipt_TextReceiptButton, String_ViewReceipt_TextEmailDemoMessage,
    String_ViewReceipt_DemoThankYou,
    String_ViewReceipt_DemoInfo,
    String_ViewReceipt_ViewReceiptButton,
    String_ViewReceipt_NoReceiptButton,
    String_System_Done,
    String_ViewReceipt_ThankYou} from '../../utilities/StringHelper';
import ErrorDialog, { ErrorDialogProps } from '../common/ErrorDialog';
import { getUrlParameter } from '../../utilities/UrlHelper';
import Loader from '../common/Loader';
import { ElementDimensions } from '../../styles/ElementDimensions';
import EmailReceiptDialog, { EmailReceiptDialogProps } from './EmailReceiptDialog';
import TextReceiptDialog, { TextReceiptDialogProps } from './TextReceiptDialog';
import { SendReceiptRequest } from '../../models/postcheckout/SendReceiptRequest';
import { SendReceiptResponse } from '../../models/postcheckout/SendReceiptResponse';
import LogoImage from '../../images/logo_footer.png';
import LogoImageCP from '../../images/logo_footer_cp.png';
import SnackBar, { SnackBarProps, SnackBarType } from '../common/SnackBar';
import { CheckOutType } from '../../models/common/CheckOutType';
import SplitButtons, { SplitButtonProps } from '../common/Buttons/SplitButtons';
import SignalR, { CheckMessageType, CheckNotificationType } from '../common/SignalR';
import MessageDialog, { MessageDialogProps } from '../common/MessageDialog';
import { GetLocationResponse } from '../../models/checkout/GetLocationResponse';
import { loadDemoReceipt } from '../../models/common/DemoData';
import FeedbackDialog, { FeedbackDialogProps } from './FeedbackDialog';
import { FeedbackType } from '../../models/common/FeedbackType';

const useStyles = makeStyles({
    buttonContainer: {
        marginTop: ElementDimensions.margin30 + "px",
    },
    buttons: {
        marginTop: ElementDimensions.margin20 + "px",
        marginBottom: ElementDimensions.margin20 + "px",
    },
    footerButtons: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        height: ElementDimensions.footerBarHeight + 'px',
        padding: ElementDimensions.padding10 + 'px ' + '0px ' + ElementDimensions.padding10 + 'px 0px',
        width: '100%',
        borderTop: '2px solid ' + themePrimary.palette.grey[200],
        backgroundColor: themePrimary.palette.primary.contrastText,
    },
    footerButtonsLandscape: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        left: 'calc(100vw *.3)',
        height: ElementDimensions.footerBarHeightLandscape + 'px',
        padding: ElementDimensions.padding10 + 'px ' + '0px ' + ElementDimensions.padding10 + 'px 0px',
        width: 'calc(100vw * .7)',
        borderTop: '2px solid ' + themePrimary.palette.grey[200],
        backgroundColor: themePrimary.palette.primary.contrastText,
    },
    footerNoButtons: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        height: '50px',
        padding: ElementDimensions.padding10 + 'px ' + '0px ' + ElementDimensions.padding10 + 'px 0px',
        width: '100%',
        borderTop: '2px solid ' + themePrimary.palette.grey[200],
        backgroundColor: themePrimary.palette.primary.contrastText,
    },
    headerContainer: {
        paddingLeft: ElementDimensions.padding10 + 'px',
        paddingRight: ElementDimensions.padding10 + 'px',
        float: 'left',
        borderRadius: '6px',
        margin: ElementDimensions.margin10 + 'px',
        width: '100%',
    },
    headerTextLeft: {
        fontSize: '1rem',
        lineHeight: '2.2rem',  
        float: 'left',
    },
    headerTextRight: {
        fontSize: '1rem',
        lineHeight: '2.2rem',  
        float: 'right',
    },
    instructionContainer: {
        textAlign: 'center',
        marginTop: ElementDimensions.margin30 + 'px ',
    },
    instructionText: {
      marginBottom: ElementDimensions.margin20 + 'px',
      whiteSpace: 'pre-line',
    },
    loadingContainer: {
        margin: ElementDimensions.margin20 + 'px',
        textAlign: 'center',
    },
    logoContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        textAlign: 'center',
        paddingTop: ElementDimensions.padding10 + 'px',
        paddingBottom: ElementDimensions.padding10 + 'px',
    },
    root: {
        height: '100%',
        overflow: 'hidden',
    },
    rootLandscape: {
        marginTop: '0',
    },
    titleContainer: {
        height: ElementDimensions.headerBarHeight + 'px',
        display: 'flex',
        textAlign: 'center',
        margin: ElementDimensions.margin10 + 'px',
    },
    titleInnerContainer: {
        gridTemplateColumns: 'calc(35vw - ' + ElementDimensions.padding10 + 'px) calc(65vw - ' + ElementDimensions.padding10 + 'px)',
        display: 'grid',
        marginTop: 'auto',
        marginBottom: 'auto',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    titleTextLeft: {
        textAlign: 'left',
        fontSize: '1.4em',
    },
    titleTextRight: {
        textAlign: 'right',
        fontSize: '1.4em',
    },
});

export interface ViewReceiptProps {
    paymentId: string | null;
}

const ViewReceipt = (props: ViewReceiptProps) => {
    let { paymentId } = props;
    const isPortrait = true;
    let history = useHistory();
    const [receipt, setReceipt] = React.useState<Receipt | null>(null);
    const [restaurantId, setRestaurantId] = React.useState<string | null>(null);
    const [snackBarOpen, setSnackBarOpen] = React.useState<boolean>(false);
    const [emailDialogOpen, setEmailDialogOpen] = React.useState<boolean>(false);
    const [feedbackDialogOpen, setFeedbackDialogOpen] = React.useState<boolean>(false);
    const [textDialogOpen, setTextDialogOpen] = React.useState<boolean>(false);
    const [errorDialogOpen, setErrorDialogOpen] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [done, setDone] = React.useState<boolean>(false);
    const [showDone, setShowDone] = React.useState<boolean>(false);
    const [isEmailedOrTextedReceipt, setIsEmailedOrTextedReceipt] = React.useState<boolean>(false);
    const [receiptSent, setReceiptSent] = React.useState<boolean>(false);
    const [sendingReceipt, setSendingReceipt] = React.useState<boolean>(false);
    const [viewReceipt, setViewReceipt] = React.useState<boolean>(false);
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [loadError, setLoadError] = React.useState<boolean>(false);
    const userState = React.useContext(UserStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const [checkOutType, setCheckOutType] = React.useState<CheckOutType | null>(null);
    const [userId, setUserId] = React.useState<number | null>(null);
    const [demoPaymentMessageShow, setDemoPaymentMessageShow] = React.useState<boolean>(false);
    const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
        primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });

    React.useEffect(() => {
        const cancelToken = axios.CancelToken;
        const source = cancelToken.source();

        var rId: string | null = getUrlParameter(history.location.search, 'r');
        if (rId === null || rId === undefined || rId === "") {
            rId = checkOutState.state.restaurantId;
        }

        setRestaurantId(rId);

        var isDemo: string | null = getUrlParameter(history.location.search, 'demo');
        var isFromLink = true;
        if (isDemo === null || isDemo === undefined || isDemo === "") {
            isFromLink = false
            isDemo = checkOutState.state.checkOutType === CheckOutType.Demo ? "1" : "0"
        }

        if (isDemo !== null && isDemo === "1" && rId !== null) {
            
            setCheckOutType(CheckOutType.Demo)

            if (isFromLink) {
                loadDataForDemo(rId, source);
            }
            else {
                setReceipt(loadDemoReceipt())
                setLoaded(true);
            }
        }
        else {
            if (paymentId === null || paymentId === undefined || paymentId === "") {
                paymentId = getUrlParameter(history.location.search, 't')
    
                if (paymentId === null || paymentId === undefined || paymentId === "") {
                    paymentId = history.location.state.paymentId;
                }
                else {
                    setIsEmailedOrTextedReceipt(true);
                }
            }
    
            let checkOutTypeQueryParam = getUrlParameter(history.location.search, 'c');
            if (checkOutTypeQueryParam !== null && checkOutTypeQueryParam !== undefined && checkOutTypeQueryParam !== '') {
                setCheckOutType(parseInt(checkOutTypeQueryParam));
            }
            else if (checkOutState.state.checkOutType !== null) {
                setCheckOutType(checkOutState.state.checkOutType);
            }
            
            if (paymentId !== null && paymentId !== undefined && rId !== null && rId !== undefined) {
                loadData(rId, source);
            }
        }
        
        return () => {
            source.cancel();
        };
    }, [paymentId]);

    const loadData = async (rId: string, cancelTokenSource: CancelTokenSource) => {

        let url: string | undefined = process.env.REACT_APP_CHECKOUT_API_URL + "/location/" + rId + "/receipt/" + paymentId!!

        const headers = {
            'X-API-Key': checkOutState.state.restaurantApiKey,
            'location_id': checkOutState.state.restaurantId
        }

        axios.get(url, { headers, cancelToken: cancelTokenSource.token })
        .then(function(response) {
            let r = response.data as GetReceiptResponse;

            const state: CheckOutState = checkOutState.state;
            state.restaurantId = r.restaurant_id;
            state.restaurantName = r.restaurant_name;
            state.restaurantLogoUrl = process.env.REACT_APP_IMAGE_URL + '/' + r.restaurant_id.toUpperCase().toString() + '.png';
            state.skin = r.skin;

            checkOutState.setState(state);
            setUserId(r.user_id);
            
            if (r.success === true) {
                setReceipt(r.receipt);
                setLoaded(true);
            }
            else {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                setErrorDialogOpen(true);
                setLoadError(true);
                setLoaded(true);
            }
        })
        .catch(function(error) {
            if (error.response) {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
            }
            else {
                setErrorMessage(String_System_ConnectionError.find(s => s.key === userState.state.locale)!!.value);
            }

            setErrorDialogOpen(true);
            setLoadError(true);
        });
    }

    const loadDataForDemo = async (rId: string, cancelTokenSource: CancelTokenSource) => {
        let url: string | undefined = process.env.REACT_APP_WEBAPP_API_URL + "/location/" + rId;

        axios.get(url!!, { cancelToken: cancelTokenSource.token })
            .then(function(response) {
                let r = response.data as GetLocationResponse;

                const state: CheckOutState = checkOutState.state;
                state.restaurantId = r.location.id;
                state.restaurantName = r.location.name;
                state.restaurantLogoUrl = process.env.REACT_APP_IMAGE_URL + '/' + r.location.id.toString() + '.png';
                state.skin = r.location.skin;

                checkOutState.setState(state);

                setReceipt(loadDemoReceipt())
                setLoaded(true)
            })
            .catch(function(error) {
                if (error.response) {
                    setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                }
                else {
                    setErrorMessage(String_System_ConnectionError.find(s => s.key === userState.state.locale)!!.value);
                }

                setErrorDialogOpen(true);
                setLoadError(true);
                setLoaded(true);
            });    
    }

    const sendReceipt = async (cancelTokenSource: CancelTokenSource, emailAddress: string | null, phoneNumber: string | null) => {
        setSendingReceipt(true);

        if (checkOutType === CheckOutType.Demo) {
            paymentId = "1234"
        }
        else {
            if (paymentId === null || paymentId === undefined || paymentId === "") {
                paymentId = getUrlParameter(history.location.search, 't')
    
                if (paymentId === null || paymentId === undefined || paymentId === "") {
                    paymentId = history.location.state.paymentId;
                }
            }
        }

        const request: SendReceiptRequest = {
            user_id: userId!!,
            table_id: null,
            email_address: emailAddress,
            phone_number: phoneNumber,
        };

        let url: string | undefined = process.env.REACT_APP_CHECKOUT_API_URL + "/location/" + restaurantId + "/receipt/" + paymentId!! + "/send"

        const headers = {
            'X-API-Key': checkOutState.state.restaurantApiKey,
            'location_id': checkOutState.state.restaurantId
        }
    
        axios.post(url, request, { headers, cancelToken: cancelTokenSource.token })
        .then(function(response) {
            let r = response.data as SendReceiptResponse;

            if (r.success === true) {
                setReceiptSent(true);
                if (checkOutState.state.feedbackType === FeedbackType.Akira) {
                    setFeedbackDialogOpen(true);
                }              
                else {
                    setSnackBarOpen(true);
                }
            }
            else {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                setErrorDialogOpen(true);
            }

            setSendingReceipt(false);
        })
        .catch(function(error) {
            if (error.response) {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
            }
            else {
                setErrorMessage(String_System_ConnectionError.find(s => s.key === userState.state.locale)!!.value);
            }

            setErrorDialogOpen(true);
            setSendingReceipt(false);
        });
    }

    const handleErrorDialogClose = (event: React.MouseEvent<HTMLElement>) => {
        setErrorDialogOpen(false);
    }

    const errorDialogProps: ErrorDialogProps = {
        open: errorDialogOpen,
        message: errorMessage,
        tryAgain: true,
        onCloseHandler: handleErrorDialogClose,
    }
    
    const handleEmailDoneButtonClick = (event: React.MouseEvent<HTMLElement>, emailAddress: string) => {
        setEmailDialogOpen(false);

        const cancelToken = axios.CancelToken;
        const source = cancelToken.source();
        sendReceipt(source, emailAddress, null);
    }

    const handleEmailCloseButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        setEmailDialogOpen(false);
    }

    const emailDialogProps: EmailReceiptDialogProps = {
        open: emailDialogOpen,
        onDoneHandler: handleEmailDoneButtonClick,
        onCloseHandler: handleEmailCloseButtonClick,
    }    

    const openEmailDialogButtonClick = () => {
        setEmailDialogOpen(true);
    }

    const emailButtonProps: FullWidthButtonProps = {
        disabled: sendingReceipt,
        label: String_ViewReceipt_EmailReceiptButton.find(s => s.key === userState.state.locale)!!.value,
        alternateColor: false,
        size: FullWidthButtonSize.Large,
        onClickHandler: openEmailDialogButtonClick,
    }

    const handleTextDoneButtonClick = (event: React.MouseEvent<HTMLElement>, phoneNumber: string) => {
        setTextDialogOpen(false);

        const cancelToken = axios.CancelToken;
        const source = cancelToken.source();
        sendReceipt(source, null, phoneNumber);
    }

    const handleTextCloseButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        setTextDialogOpen(false);
    }

    const textDialogProps: TextReceiptDialogProps = {
        open: textDialogOpen,
        onDoneHandler: handleTextDoneButtonClick,
        onCloseHandler: handleTextCloseButtonClick,
    } 

    const handleFeedbackDoneButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        setFeedbackDialogOpen(false);
        window.location.href = checkOutState.state.akiraSurveyUrl!!;
    }

    const handleFeedbackCloseButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        setFeedbackDialogOpen(false);
        setViewReceipt(false)
    }

    const feedbackDialogProps: FeedbackDialogProps = {
        open: feedbackDialogOpen,
        receiptSent: receiptSent,
        onDoneHandler: handleFeedbackDoneButtonClick,
        onCloseHandler: handleFeedbackCloseButtonClick,
    }

    const snackBarCloseHandler = () => {
        setSnackBarOpen(false);
    }

    const snackBarProps: SnackBarProps = {
        message: String_SendReceiptLink_SuccessMessage.find(s => s.key === userState.state.locale)!!.value,
        type: SnackBarType.Success,
        open: snackBarOpen,
        onCloseHandler: snackBarCloseHandler
    }

    const signalRInitializedHandler = () => {
        
    }

    const signalRNotificationReceivedHandler = (type: CheckNotificationType) => {
        if (type === CheckNotificationType.AmountClaimed) {

        }
        else if (type === CheckNotificationType.PaymentCompleted) {

        }
        else if (type === CheckNotificationType.CheckClosed) {
            
        }
        else if (type === CheckNotificationType.ClaimExpired) {
            
        }
    }

    const handleMessageDialogClose = (event: React.MouseEvent<HTMLElement>) => {
        setDemoPaymentMessageShow(false);
    }

    const messageDialogProps: MessageDialogProps = {
        open: demoPaymentMessageShow,
        message: String_ViewReceipt_TextEmailDemoMessage.find(s => s.key === userState.state.locale)!!.value,
        onCloseHandler: handleMessageDialogClose,
    }


    const handleViewReceiptButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        setViewReceipt(true)
        setShowDone(true)
    }

    const viewReceiptButtonProps: FullWidthButtonProps = {
        disabled: false,
        label: String_ViewReceipt_ViewReceiptButton.find(s => s.key === userState.state.locale)!!.value,
        alternateColor: false,
        size: FullWidthButtonSize.Large,
        onClickHandler: handleViewReceiptButtonClick,
    }

    const handleNoReceiptButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        setDone(true)
        if (checkOutState.state.feedbackType === FeedbackType.Akira) {
            setFeedbackDialogOpen(true);
        }  
    }

    const noReceiptButtonProps: FullWidthButtonProps = {
        disabled: false,
        label: String_ViewReceipt_NoReceiptButton.find(s => s.key === userState.state.locale)!!.value,
        alternateColor: false,
        size: FullWidthButtonSize.Large,
        onClickHandler: handleNoReceiptButtonClick,
    }

    const handleTextReceiptButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        setTextDialogOpen(true)
    }

    const textReceiptButtonProps: FullWidthButtonProps = {
        disabled: false,
        label: String_ViewReceipt_TextReceiptButton.find(s => s.key === userState.state.locale)!!.value,
        alternateColor: false,
        size: FullWidthButtonSize.Large,
        onClickHandler: handleTextReceiptButtonClick,
    }

    const handleEmailReceiptButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        setEmailDialogOpen(true)
    }

    const emailReceiptButtonProps: FullWidthButtonProps = {
        disabled: false,
        label: String_ViewReceipt_EmailReceiptButton.find(s => s.key === userState.state.locale)!!.value,
        alternateColor: false,
        size: FullWidthButtonSize.Large,
        onClickHandler: handleEmailReceiptButtonClick,
    }

    const handleDoneButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!viewReceipt) {
            setDone(true)
        }
        else {
            setViewReceipt(false)
            if (checkOutState.state.feedbackType === FeedbackType.Akira) {
                setFeedbackDialogOpen(true);
            }
        }
    }
    
    const doneButtonProps: FullWidthButtonProps = {
        disabled: false,
        label: String_System_Done.find(s => s.key === userState.state.locale)!!.value,
        alternateColor: false,
        size: FullWidthButtonSize.Large,
        onClickHandler: handleDoneButtonClick,
    }

    return (
        <div className={clsx(classes.root, {
            [classes.rootLandscape]: !isPortrait,
        })}>
            {!loaded && !loadError &&
                <div className={classes.loadingContainer}>
                    {!loaded &&
                        <Loader />                    
                    }
                </div>
            }

            {loaded && !loadError && !viewReceipt && !done && !isEmailedOrTextedReceipt &&
                <div className={classes.buttonContainer}>
                    <div className={classes.buttons}>
                        <FullWidthButton 
                            disabled={viewReceiptButtonProps.disabled}
                            label={viewReceiptButtonProps.label}
                            alternateColor={viewReceiptButtonProps.alternateColor}
                            size={viewReceiptButtonProps.size}
                            onClickHandler={viewReceiptButtonProps.onClickHandler}/>
                    </div>
                    <div className={classes.buttons}>
                        <FullWidthButton 
                            disabled={textReceiptButtonProps.disabled}
                            label={textReceiptButtonProps.label}
                            alternateColor={textReceiptButtonProps.alternateColor}
                            size={textReceiptButtonProps.size}
                            onClickHandler={textReceiptButtonProps.onClickHandler}/>
                    </div>
                    <div className={classes.buttons}>
                        <FullWidthButton 
                            disabled={emailReceiptButtonProps.disabled}
                            label={emailReceiptButtonProps.label}
                            alternateColor={emailReceiptButtonProps.alternateColor}
                            size={emailReceiptButtonProps.size}
                            onClickHandler={emailReceiptButtonProps.onClickHandler}/>
                    </div>
                    <div className={classes.buttons}>
                        <FullWidthButton 
                            disabled={noReceiptButtonProps.disabled}
                            label={noReceiptButtonProps.label}
                            alternateColor={noReceiptButtonProps.alternateColor}
                            size={noReceiptButtonProps.size}
                            onClickHandler={noReceiptButtonProps.onClickHandler}/>
                    </div>
                </div>
            }

            {loaded && !loadError && !done && (viewReceipt || isEmailedOrTextedReceipt) &&
                <div>
                    {receipt !== null && receipt !== undefined &&
                        <div>
                            <CheckReceipt receipt={receipt}
                                hasFooterBar={true}
                                isDemo={checkOutType === CheckOutType.Demo}/>
                        </div>
                    }

                </div>
            }

            {loaded && !loadError && done && !isEmailedOrTextedReceipt &&
                <div className={classes.instructionContainer}>
                    <Typography variant="h4" className={classes.instructionText}>
                        {String_ViewReceipt_ThankYou.find(s => s.key === userState.state.locale)!!.value}
                    </Typography>
                </div>
            }

            {loaded && !loadError && !isEmailedOrTextedReceipt &&
                <div>
                    {showDone && !done &&
                        <div className={isPortrait ? classes.footerButtons : classes.footerButtonsLandscape}>
                            <FullWidthButton 
                                disabled={doneButtonProps.disabled}
                                label={doneButtonProps.label}
                                alternateColor={doneButtonProps.alternateColor}
                                size={doneButtonProps.size}
                                onClickHandler={doneButtonProps.onClickHandler}/>
                        </div>
                    }

                    <EmailReceiptDialog 
                        open={emailDialogProps.open}
                        onCloseHandler={emailDialogProps.onCloseHandler}
                        onDoneHandler={emailDialogProps.onDoneHandler} />

                    <TextReceiptDialog 
                        open={textDialogProps.open}
                        onCloseHandler={textDialogProps.onCloseHandler}
                        onDoneHandler={textDialogProps.onDoneHandler} />

                    <FeedbackDialog 
                        open={feedbackDialogProps.open}
                        receiptSent={feedbackDialogProps.receiptSent}
                        onCloseHandler={feedbackDialogProps.onCloseHandler}
                        onDoneHandler={feedbackDialogProps.onDoneHandler} />
                </div>
            }

            <MessageDialog 
                open={messageDialogProps.open}
                message={messageDialogProps.message}
                onCloseHandler={messageDialogProps.onCloseHandler} />

            <ErrorDialog 
                open={errorDialogProps.open}
                message={errorDialogProps.message}
                tryAgain={errorDialogProps.tryAgain}
                onCloseHandler={errorDialogProps.onCloseHandler} />

            <SnackBar 
                message={snackBarProps.message}
                type={snackBarProps.type}
                open={snackBarProps.open}
                onCloseHandler={snackBarProps.onCloseHandler} />

            {checkOutState.state.checks !== null && 
                <SignalR 
                    oldChecks={null}
                    messageType={CheckMessageType.Unsubscribe}
                    load={true}
                    refreshCheck={false}
                    onNotificationReceived={signalRNotificationReceivedHandler}
                    onInitialized={signalRInitializedHandler} />
            }

        </div>
    );
};

export default ViewReceipt;